import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable, of} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {shareReplay} from 'rxjs/operators';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {PageItem} from '@models/sidebar/page-item';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';

@Injectable({
  providedIn: 'root'
})
export class StatusEventPageService {

  private LIST_STATUS_EVENT_SIGNALS_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage';
  private LIST_STATUS_EVENT_PAGES_OF_MACHINE_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/listPages';
  private GET_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId';
  private SAVE_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId';
  private CLONE_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId/clone/:templatePage';
  private DELETE_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/:pageId/baseId/:itemId';
  private RENAME_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineId/statusEventPage/rename/baseId/:itemId';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';

  private readonly BASE_URL: string;
  private statusPageCache = new Map();
  private statusPageSignalsCache = new Map();
  private statusPagesListCache = new Map();

  constructor(private http: HttpClient, private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private globalHelper: GlobalHelperService,) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating status page.');
    this.successCreatingPage = this.translateService.instant('Status page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming status page.');
    this.successRenamingPage = this.translateService.instant('Status page was renamed.');
  }

  public listStatusEventSignals(machineId: string): Observable<any> {
    let url: string = this.BASE_URL + this.LIST_STATUS_EVENT_SIGNALS_URL_SUFFIX;
    url = url.replace(':machineId', machineId);

    const signalsListFromCache = this.statusPageSignalsCache.get(url);
    if (signalsListFromCache) {
      return of(signalsListFromCache);
    }

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.statusPageSignalsCache.set(url, result);
    });
    return response;
  }

  public listStatusEventPagesOfMachine(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.LIST_STATUS_EVENT_PAGES_OF_MACHINE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId));

    const statusPagesListFromCache = this.statusPagesListCache.get(url);
    if (statusPagesListFromCache) {
      return of(statusPagesListFromCache);
    }

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.statusPagesListCache.set(url, result);
    });
    return response;
  }

  public getStatusEventPage(machineId: string, pageId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    url = url.replace(':pageId', pageId);
    const errorPageFromCache = this.statusPageCache.get(url);
    if (errorPageFromCache) {
      return of(errorPageFromCache);
    }

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.statusPageCache.set(url, result);
    });
    return response;
  }


  public create(baseMenuItems: PageItem[], pageItem: PageItem, machineId: string | null, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean): void {
    if (machineId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      //tabId has to be -1 if error page is created
      const errorPage = new StatusEventPage(-1, pageItem.name);
      this.saveEventStatusPage(machineId, '-1', errorPage).subscribe((baseMenuItem: BaseMenuItem) => {
        this.handleCreatedPage(baseMenuItem, pageItem, baseMenuItems, currentPageName, parentId, isAdminOrHigher);
      }, (error) => {
        this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  public update(machineId: string, pageId: string, statusEventPage: StatusEventPage): Observable<any> {
    return this.saveEventStatusPage(machineId, pageId, statusEventPage);
  }

  public deleteEventStatusPage(machineId: string, pageId: string, itemId: string): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':pageId', pageId)
    .replace(':itemId', itemId);
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public rename(pageItem: PageItem, machineId: string | null, parentId: string | null, currentPageName: string, nameFallback: Object): void {
    if (machineId && pageItem.id && pageItem.pageId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const errorTab = new StatusEventPage(Number(pageItem.pageId), pageItem.name);
      const itemId: string = pageItem.id;
      this.renameEventStatusPage(machineId, itemId, errorTab).subscribe((result: BaseMenuItem) => {
        this.globalHelper.handleUpdatedPage(result.name, parentId, currentPageName, itemId, this.successRenamingPage);
      }, (error) => {
        this.leftMenuService.broadcastErrorUpdatePageChange(nameFallback);
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  public startStatusEventPageCloningProcess(machineId: string, pageId: string, isTemplatePage:boolean,targetMachines: number[]) {
    let url: string = this.BASE_URL + this.CLONE_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId)).replace(':pageId', String(pageId)).replace(':templatePage', String(isTemplatePage));
    return this.http.post<StatusEventPage>(url, targetMachines, HTTPS_REQUEST_OPTIONS);
  }

  private saveEventStatusPage(machineId: string, pageId: string, request: StatusEventPage): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId)).replace(':pageId', String(pageId));
    return this.http.post<StatusEventPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], currentPageName: string, parentId: string, isAdminOrHigher: boolean) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  private renameEventStatusPage(machineId: string, itemId: string, request: StatusEventPage): Observable<any> {
    let url: string = this.BASE_URL + this.RENAME_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':itemId', itemId);
    return this.http.post<StatusEventPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }
}
